import { UserAgent, Registerer, RegistererState } from 'sip.js';
import { GUDHUB_SETTINGS } from '../../../../gudhub_settings.js';
import { patchTransportSendSpacing } from './transportSendSpacing.js';
import { traceCall } from './callTrace.js';

// Re-register this many ms before the GudHub access token (used as the SIP
// digest auth username) actually expires, so a stale token never gets a
// chance to fail a REGISTER.
const TOKEN_REFRESH_MARGIN = 60000;

// How often to send an OPTIONS liveness probe while idle/registered, and how
// long to wait for a response before treating the connection as dead. A
// dead-but-not-yet-detected WSS socket (e.g. the browser's outbound writes
// still appear to succeed after the OS sleeps, but nothing ever comes back -
// see handleTransportDisconnect's comment) never fires sip.js's own
// onDisconnect, so this is the only thing that actually notices. Built on a
// stateless out-of-dialog OPTIONS (sendOptionsPing) rather than a REGISTER
// refresh - a prior REGISTER-based version of this heartbeat was found live
// to collide with in-flight INVITE signaling on the same WSS connection
// (matched SIP.js's ~32s INVITE transaction Timer B); OPTIONS never touches
// the Registerer or any call dialog, so it can't do that.
const HEARTBEAT_INTERVAL = 10000;
// Exported so startOutgoingCall (callSessionMixin.js) can run the same probe
// as a pre-flight check right before dialing - see its comment for why.
export const HEARTBEAT_TIMEOUT = 8000;

const SIP_TAB_SESSION_STORAGE_KEY = 'gudhub_sip_tab_session_id';

// Identifies this browser TAB (not this user) for as long as it stays open -
// sessionStorage is per-tab, unlike localStorage, so two tabs open to the
// same GudHub account each get their own id. Used to give each tab its own
// distinct SIP AOR (see registerSip below) instead of every open tab/device
// registering as literally the same "<userId>@domain" - the old scheme let
// FreeSWITCH's registrar (multiple-registrations="false") keep only the
// newest tab's registration alive, silently unregistering every other one
// mid-session, which is what made SIP connections look "unstable" whenever a
// user had more than one tab or device open at once. A distinct AOR per tab
// makes each one FreeSWITCH's own genuine, independent registration - the
// server then rings all of them for an inbound call and cancels the rest
// once one answers (see sip-gudhub-server's dialplan.js buildUserBridgeTarget).
function getSipTabSessionId() {
    try {
        let id = sessionStorage.getItem(SIP_TAB_SESSION_STORAGE_KEY);
        if(!id) {
            id = Math.random().toString(36).slice(2, 10);
            sessionStorage.setItem(SIP_TAB_SESSION_STORAGE_KEY, id);
        }
        return id;
    } catch (err) {
        // sessionStorage can throw in some restricted contexts (e.g. certain
        // private-browsing modes) - fall back to a fresh id rather than
        // letting SIP registration itself fail. Worst case this tab gets a
        // new AOR on every registerSip() call instead of a stable one, which
        // only means a stale registration briefly lingers until it expires
        // server-side, not a functional break.
        return Math.random().toString(36).slice(2, 10);
    }
}

export const sipRegistrationMixin = {

    //********************* REGISTER SIP ********************************//
    // Builds a persistent SIP.js UserAgent + Registerer for the active user, so
    // this browser tab can receive incoming INVITEs regardless of which view is
    // open (see handleIncomingInvite in callSessionMixin). Stateless auth - no
    // HTTP call to sip-gudhub-server first: the client's own GudHub access
    // token goes in authorizationUsername (sent in the clear by SIP digest;
    // server/freeswitch-xml/directory.js decrypts and validates it directly),
    // and authorizationPassword is a fixed, shared, non-secret value every
    // client uses (see sip-gudhub-server/docs/client-calling/gudhub-user-to-gudhub-user.md).
    //
    // One registration at a time. onInit's retry loop, a native wake-up
    // (nativeCallsMixin.ensureRegisteredForNativeCall) and the reconnect paths
    // can all ask for one within the same second of a cold start from a push.
    // The userAgent guard below is only set after an await, so each of them
    // used to build its own UserAgent, overwrite this.userAgent, and let its
    // failure path stop whichever agent was current - including the one
    // carrying the call being answered. Concurrent callers now share one
    // attempt and its outcome.
    registerSip() {
        this._registration ??= this._registerSip().finally(() => {
            this._registration = null;
        });
        return this._registration;
    },

    async _registerSip() {
        if(this.userAgent) return;

        const domain = GUDHUB_SETTINGS.sip_domain;
        this.sipDomain = domain;
        // This tab's own distinct SIP identity - see getSipTabSessionId's
        // own comment. Recomputed on every registerSip() call (e.g. after a
        // token-refresh unregister/register cycle) rather than cached once,
        // so a mid-session account switch (unregisterSip then registerSip
        // again as a different activeUserId) always reflects the currently
        // active user, while still keeping the same per-tab session suffix.
        this.sipAorUser = `${this.activeUserId}-${getSipTabSessionId()}`;

        try {
            this.userAgent = new UserAgent({
                uri: UserAgent.makeURI(`sip:${this.sipAorUser}@${domain}`),
                transportOptions: { server: GUDHUB_SETTINGS.sip_ws_uri },
                authorizationUsername: await gudhub.getToken(),
                authorizationPassword: GUDHUB_SETTINGS.sip_digest_shared_secret,
                // The caller's name travels in the From header - the only place a phone
                // woken by push, with no GudHub session yet, can read it from (the wake
                // push's caller_name is taken from it by the SIP server's dialplan).
                displayName: gudhub.storage.user?.fullname || undefined,
                logConfiguration: false,
                logLevel: 'error',
                delegate: {
                    onInvite: (invitation) => this.handleIncomingInvite(invitation),
                    onDisconnect: (error) => this.handleTransportDisconnect(error)
                }
            });

            // See transportSendSpacing.js - works around a live-confirmed
            // FreeSWITCH bug where two WebSocket writes landing in the same
            // socket read (e.g. sip.js's own auth-retry ACK+INVITE burst) can
            // leave the second message unprocessed for ~30-40s.
            patchTransportSendSpacing(this.userAgent.transport);

            await this.userAgent.start();

            // iOS: incoming calls belong to the app's native call engine
            // (nativeEngineMixin.js), which registers its own AOR whenever a
            // push wakes the phone. This agent only places outgoing calls, so
            // it connects without registering - registering as well would have
            // every call to this user delivered to the phone twice.
            if(!this.nativeEngine) {
                this.registerer = new Registerer(this.userAgent);
                this.registerer.stateChange.addListener((state) => this.handleRegistererStateChange(state));

                // Registerer.register() resolves once the REGISTER request is sent, not
                // once a final response arrives - wait for the actual REGISTER response
                // before considering this done. Timeout guards against a request that
                // never gets a response at all (e.g. transport black hole) hanging here
                // forever.
                await this.waitForRegistered(10000);
            }
        } catch (err) {
            // A UserAgent left non-null after a failed attempt makes the guard
            // above treat the NEXT registerSip() call as "already registered" and
            // silently no-op forever - this is exactly what let
            // handleTransportDisconnect's retry loop "succeed" after doing real
            // work only once. Tear down any partial state before rethrowing so
            // every retry is a genuine fresh attempt.
            traceCall('registerFailed', { aor: this.sipAorUser, error: err?.message });
            await this.unregisterSip();
            throw err;
        }

        this.callState = 'registered';
        this.scheduleTokenRefresh();
        traceCall('registered', { aor: this.sipAorUser });
    },

    //********************* WAIT FOR REGISTERED ********************************//
    // Sends a REGISTER and waits for its actual response, used right after
    // building a fresh Registerer (registerSip). Deliberately uses
    // register()'s per-request requestDelegate (onAccept/onReject) rather
    // than the Registerer's stateChange event: stateChange only fires on a
    // *transition* into Registered (see sip.js's Registerer.registered(),
    // which no-ops if already in that state), so a REGISTER sent while
    // already Registered would never re-fire it.
    waitForRegistered(timeoutMs) {
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                reject(new Error('SIP registration timed out'));
            }, timeoutMs);

            this.registerer.register({
                requestDelegate: {
                    onAccept: () => {
                        clearTimeout(timer);
                        resolve();
                    },
                    onReject: (response) => {
                        clearTimeout(timer);
                        reject(new Error(`SIP registration failed (status ${response.message.statusCode})`));
                    }
                }
            }).catch((err) => {
                clearTimeout(timer);
                reject(err);
            });
        });
    },

    //********************* SCHEDULE TOKEN REFRESH ********************************//
    // The GudHub access token doubles as the SIP digest auth username and it
    // expires - re-register with a fresh one shortly before that happens.
    // SIP.js's UserAgent doesn't support hot-swapping authorizationUsername, so
    // a clean unregister/register cycle is the simplest correct way to rotate it.
    scheduleTokenRefresh() {
        clearTimeout(this._tokenRefreshTimer);

        const expirydate = gudhub.storage.getUser()?.expirydate;
        const expiresAt = expirydate ? new Date(expirydate).getTime() : Date.now() + 300000;
        const delay = Math.max(expiresAt - Date.now() - TOKEN_REFRESH_MARGIN, 0);
        this._tokenRefreshTimer = setTimeout(() => this.refreshSipRegistration(), delay);
    },

    //********************* REFRESH SIP REGISTRATION ********************************//
    async refreshSipRegistration() {
        // Don't tear down the UserAgent out from under a ringing/active call -
        // retry shortly after instead of interrupting it. Deliberately NOT
        // holding the exclusive reconnect lock during this wait: if the
        // transport is actually dead during this call, handleTransportDisconnect
        // must still be free to tear the call down and recover - only the
        // actual unregister+register work below needs mutual exclusion.
        if(this.callState !== 'registered') {
            this._tokenRefreshTimer = setTimeout(() => this.refreshSipRegistration(), 5000);
            return;
        }

        return this.runExclusiveReconnect(async () => {
            try {
                await this.unregisterSip();
                await this.registerSip();
                // Restores sipEnabled/the heartbeat if an EARLIER failure (this
                // same function, or handleTransportDisconnect) had cleared them -
                // neither of those failure paths has a matching success path of
                // its own to undo it, so a since-recovered connection could
                // otherwise stay stuck reporting "calling unavailable" forever
                // (confirmed live: registration and message flow both fully
                // recovered after a reconnect, but sipEnabled never went back to
                // true, so start_call kept refusing every call). startHeartbeat
                // is idempotent (see its own guard) - harmless if it was never
                // stopped to begin with.
                this.sipEnabled = true;
                this.startHeartbeat();
            } catch (err) {
                console.error('SIP token refresh failed', err);
                this.sipEnabled = false;
                this.stopHeartbeat();
                this.toast?.customToast({
                    template: `<md-toast><p>Calling is unavailable right now (registration failed).</p></md-toast>`,
                    hideDelay: 6000
                });
            }
        });
    },

    //********************* RUN EXCLUSIVE RECONNECT ********************************//
    // Ensures handleTransportDisconnect and refreshSipRegistration never run
    // their actual unregister+register work concurrently - both mutate
    // this.userAgent/this.registerer, and their timer chains (10s heartbeat,
    // token-refresh) can both become overdue and fire within moments of each
    // other after this tab was suspended for a while (e.g. laptop sleep).
    // Whichever caller arrives first "wins" the promise slot; any other caller
    // during that window just waits on the same promise instead of starting a
    // second, competing attempt alongside it.
    runExclusiveReconnect(fn) {
        if(this._reconnectPromise) return this._reconnectPromise;
        this._reconnectPromise = fn().finally(() => {
            this._reconnectPromise = null;
        });
        return this._reconnectPromise;
    },

    //********************* HANDLE TRANSPORT DISCONNECT ********************************//
    // The WSS socket can die server-side (nginx/FreeSWITCH restart, network
    // change, machine sleep) while this tab still believes it is registered.
    // Observed live: FreeSWITCH could still READ from the half-dead socket
    // (the INVITE arrived) but every write back failed, so the call got no
    // response at all - it just silently did nothing, and the stuck session
    // then threw "Invalid session state Terminating" on cancel. Reset any
    // in-flight call and re-register on a fresh connection.
    async handleTransportDisconnect(error) {
        if(this._shuttingDown) return;
        console.warn('SIP transport disconnected - reconnecting', error);
        traceCall('transportDisconnect', { error: error?.message, callState: this.callState });

        return this.runExclusiveReconnect(async () => {
            if(this.session) this.teardownCall();
            await this.unregisterSip();

            // 30 attempts x 10s = 5 minutes of cover. The previous cap of 5
            // (~50s) was shorter than a routine FreeSWITCH service restart,
            // so every deploy left all tabs permanently unregistered until a
            // manual reload.
            for(let attempt = 1; attempt <= 30; attempt++) {
                try {
                    await this.registerSip();
                    // See refreshSipRegistration's own comment on this same
                    // pair of calls - without it, a connection that recovers
                    // here after sipEnabled was already false (e.g. this
                    // same disconnect handler ran once before and gave up,
                    // or a token refresh failed first) stays stuck reporting
                    // "calling unavailable" even though registration and
                    // message flow are both genuinely healthy again.
                    this.sipEnabled = true;
                    this.startHeartbeat();
                    return;
                } catch (err) {
                    console.error(`SIP reconnect attempt ${attempt} failed`, err);
                    await new Promise((resolve) => setTimeout(resolve, 10000));
                }
            }

            this.sipEnabled = false;
            this.stopHeartbeat();
            this.toast?.customToast({
                template: `<md-toast><p>Calling connection lost - reload the page to restore it.</p></md-toast>`,
                hideDelay: 8000
            });
        });
    },

    //********************* START HEARTBEAT ********************************//
    startHeartbeat() {
        if(this._heartbeatTimer) return;
        this._heartbeatTimer = setInterval(() => this.sendHeartbeat(), HEARTBEAT_INTERVAL);

        // setInterval/setTimeout don't just pause cleanly across OS sleep - the
        // renderer's own timer clock can stay drifted behind wall-clock time by
        // roughly the sleep duration after resume, so the next "10s" heartbeat
        // tick can actually be many minutes late (confirmed live: WSS OPTIONS
        // traffic timestamps still ~12min behind real time well after a resume).
        // visibilitychange fires immediately at the real moment the tab becomes
        // visible again, independent of that drift - force an out-of-band check
        // right then instead of waiting for the drifted interval to catch up.
        this._visibilityChangeHandler = () => {
            if(document.visibilityState === 'visible') this.sendHeartbeat();
        };
        document.addEventListener('visibilitychange', this._visibilityChangeHandler);
    },

    //********************* STOP HEARTBEAT ********************************//
    stopHeartbeat() {
        clearInterval(this._heartbeatTimer);
        this._heartbeatTimer = null;

        document.removeEventListener('visibilitychange', this._visibilityChangeHandler);
        this._visibilityChangeHandler = null;
    },

    //********************* SEND HEARTBEAT ********************************//
    async sendHeartbeat() {
        if(!this.userAgent || this._reconnectPromise || this._shuttingDown || this._heartbeatInFlight) return;
        // Don't perturb a ringing/active call on a heartbeat - a genuinely dead
        // transport during a call is still caught by handleTransportDisconnect's
        // own onDisconnect trigger.
        if(this.callState !== 'registered' && this.callState !== 'idle') return;

        // The Registerer can silently drift out of Registered (e.g. server-side
        // expiry) without callState catching up - ponging a dead registration
        // with OPTIONS wouldn't fix it, so re-register instead of pinging.
        // Route through handleTransportDisconnect rather than
        // refreshSipRegistration: the latter is a graceful token-rotation
        // helper with no retry/backoff/give-up semantics of its own, so it
        // can't recover a truly dead registration. handleTransportDisconnect
        // already owns that recovery, and the shared reconnect lock makes it
        // safe to call even if a refresh happens to be in flight.
        // Only an agent that registers can drift out of Registered. In the iOS
        // shell this one never does (nativeEngineMixin.js), and checking it
        // there tore the connection down and rebuilt it every 10 seconds.
        if(!this.nativeEngine && this.registerer?.state !== RegistererState.Registered) {
            this.handleTransportDisconnect(new Error('SIP registerer drifted out of Registered state'));
            return;
        }

        this._heartbeatInFlight = true;
        try {
            await this.sendOptionsPing(HEARTBEAT_TIMEOUT);
        } catch (err) {
            console.warn('SIP heartbeat (OPTIONS) failed - connection appears dead', err);
            this.handleTransportDisconnect(err);
        } finally {
            this._heartbeatInFlight = false;
        }
    },

    //********************* SEND OPTIONS PING ********************************//
    // Sends a stateless out-of-dialog OPTIONS to this user's own URI as a
    // liveness probe, via sip.js's low-level UserAgentCore (there's no
    // high-level Registerer/Inviter-style wrapper for OPTIONS). Self-pinging
    // exercises the full round trip through FreeSWITCH back to this same
    // browser tab, closest to what a REGISTER refresh effectively verified.
    sendOptionsPing(timeoutMs) {
        return new Promise((resolve, reject) => {
            const core = this.userAgent.userAgentCore;
            // Must be this tab's own registered AOR (sipAorUser), not the
            // bare activeUserId - FreeSWITCH's registrar now only knows this
            // tab by its per-tab AOR (see registerSip), so pinging the bare
            // user id would 404 even though this tab's own registration is
            // perfectly healthy.
            const target = UserAgent.makeURI(`sip:${this.sipAorUser}@${this.sipDomain}`);
            const message = core.makeOutgoingRequestMessage('OPTIONS', target, this.userAgent.configuration.uri, target, {});

            const timer = setTimeout(() => reject(new Error('SIP OPTIONS ping timed out')), timeoutMs);

            core.request(message, {
                onAccept: () => { clearTimeout(timer); resolve(); },
                onReject: (response) => { clearTimeout(timer); reject(new Error(`SIP OPTIONS ping rejected (status ${response.message.statusCode})`)); }
            });
        });
    },

    //********************* HANDLE REGISTERER STATE CHANGE ********************************//
    // Keeps callState in sync with the Registerer's actual state after the
    // initial registration (e.g. a dropped/expired registration later on).
    // Doesn't touch callState while a call is ringing/active, to avoid a
    // transient re-register cycle interrupting the call UI.
    handleRegistererStateChange(state) {
        if(state === RegistererState.Registered && this.callState === 'idle') {
            this.callState = 'registered';
        } else if((state === RegistererState.Unregistered || state === RegistererState.Terminated) && this.callState === 'registered') {
            this.callState = 'idle';
        }
    },

    //********************* UNREGISTER SIP ********************************//
    async unregisterSip() {
        clearTimeout(this._tokenRefreshTimer);

        // userAgent.stop() fires the transport's onDisconnect - flag this as
        // an intentional shutdown so handleTransportDisconnect doesn't treat
        // it as a dead socket and start a competing re-register cycle.
        this._shuttingDown = true;
        try {
            if(this.registerer) {
                await this.registerer.unregister().catch(() => {});
                this.registerer = null;
            }

            if(this.userAgent) {
                await this.userAgent.stop().catch(() => {});
                this.userAgent = null;
            }
        } finally {
            this._shuttingDown = false;
        }

        this.callState = 'idle';
    },
};
